import { FC, ReactNode } from "react";
import { Banner } from "@jobber/components/Banner";
import { Button } from "@jobber/components/Button";
import { Content } from "@jobber/components/Content";
import { Heading } from "@jobber/components/Heading";
import { Text } from "@jobber/components/Text";
import { useBreakpoints } from "@jobber/hooks/useBreakpoints";

interface FlowButton {
    text: string;
    onClick?: () => void;
    url?: string;
    disabled?: boolean;
    loading?: boolean;
}

interface AuthFlowFrameProps {
    readonly pretitle: string;
    readonly title: ReactNode;
    readonly description?: ReactNode;
    readonly children?: ReactNode;
    readonly primaryButton?: FlowButton;
    readonly secondaryButton?: FlowButton;
    readonly footer?: ReactNode;
    readonly serverError?: string | null;
    readonly sidePanel?: ReactNode;
}

const AuthFlowFrame: FC<AuthFlowFrameProps> = ({
    pretitle,
    title,
    description,
    children,
    primaryButton,
    secondaryButton,
    footer,
    serverError,
    sidePanel,
}) => {
    const { mediumAndUp } = useBreakpoints();

    return (
        <div
            style={{
                width: "100%",
                padding: mediumAndUp ? "40px" : "24px",
                display: "flex",
                flexDirection: "column",
                gap: "32px",
            }}
        >
            <div
                style={{
                    display: "flex",
                    gap: "40px",
                    flexDirection: mediumAndUp ? "row" : "column-reverse",
                    justifyContent: "space-between",
                }}
            >
                {/* Left column: content */}
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Content spacing="small">
                        <Heading level={4}>{pretitle}</Heading>
                        <Heading level={1}>{title}</Heading>
                    </Content>

                    {description && (
                        <div style={{ marginTop: "16px", maxWidth: "440px" }}>
                            <Text size="base">{description}</Text>
                        </div>
                    )}

                    {children && (
                        <div style={{ marginTop: "32px" }}>{children}</div>
                    )}

                    {serverError && (
                        <div
                            style={{
                                marginTop: "24px",
                                maxWidth: "440px",
                            }}
                        >
                            <Banner type="error" icon="alert">
                                {serverError}
                            </Banner>
                        </div>
                    )}

                    <div
                        style={{
                            display: "flex",
                            gap: "var(--space-small)",
                            alignItems: "center",
                            marginTop: "32px",
                        }}
                    >
                        {secondaryButton && (
                            <Button
                                label={secondaryButton.text}
                                type="secondary"
                                url={secondaryButton.url}
                                onClick={secondaryButton.onClick}
                                disabled={secondaryButton.disabled}
                                loading={secondaryButton.loading}
                            />
                        )}
                        {primaryButton && (
                            <Button
                                label={primaryButton.text}
                                type="primary"
                                url={primaryButton.url}
                                onClick={primaryButton.onClick}
                                disabled={primaryButton.disabled}
                                loading={primaryButton.loading}
                            />
                        )}
                    </div>

                    {footer && (
                        <div style={{ marginTop: "24px" }}>{footer}</div>
                    )}
                </div>

                {/* Right column: side panel — pages pass this in as a prop.
                    For this integration every page uses the same promotional
                    image; other integrations/steps can pass different content. */}
                {sidePanel}
            </div>
        </div>
    );
};

export default AuthFlowFrame;
